/*
 * Pre-checkout del mini-cart: hoja que sube desde abajo al pulsar «Continuar». Mismo
 * funcionamiento que el mini-cart de carolina-store, más los datos que antes pedía /cart:
 *   Entrega: envío a domicilio (ubigeo, dirección, referencia; envío regular o programado con
 *            fecha y horario) o retiro en tienda (con el stock del carrito en cada tienda).
 *   Tus datos: nombres, apellidos, email y celular.
 *   Comprobante: documento de identidad y «Necesito factura» (RUC).
 * Al continuar:
 *   1. cart/update.js con los atributos que ya usaba /cart (Modalidad despacho, ubigeo, nombre,
 *      Documento Identidad, ruc…): el pedido llega con los mismos datos que antes.
 *   2. cartCreate en la Storefront API con los atributos por línea que lee «Reveni Envíos», la
 *      CarrierService que cotiza Lima y Callao (_ubigeo y el plazo o la fecha programada en
 *      _dispatch_mode_description, que el checkout muestra bajo el método), email y celular del
 *      comprador, y la dirección de envío o la tienda de retiro preseleccionada (pickupHandle).
 *      Todas con guion bajo, para que el checkout no las muestre bajo cada producto.
 *   3. Redirección al checkout (+ UTMs). Si la Storefront API falla dos veces, cae al checkout
 *      nativo con la dirección en la URL, como hacía /cart.
 * Los departamentos, provincias y distritos salen de assets/enova-ubigeo-pe.js (INEI), y las
 * tarifas de Shopify (Ajax API del tema), las mismas que dará el checkout.
 * Comparte con /cart la clave `customerData` de localStorage (mismos campos).
 */
(() => {
  const CFG = window.PRECHECKOUT_CONFIG;
  if (!CFG) return;

  const COUNTRY = CFG.country || 'PE';
  const CUSTOMER_KEY = 'customerData';
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
  // Storefront API sin token, en el dominio de la tienda: Shopify la abre así a cualquier tienda sin
  // contraseña. El token público que se usaba lo revocaron el 11-oct-2026 y cartCreate empezó a dar
  // 401; sin token no hay nada que se pueda revocar.
  const STOREFRONT_URL = `${window.location.origin}/api/${CFG.apiVersion}/graphql.json`;
  // Envío gratis desde este total del carrito (céntimos): el descuento automático de Shopify deja
  // el envío en S/ 0 en el checkout aunque la tarifa traiga precio. 0 = sin envío gratis.
  const FREE_SHIPPING_THRESHOLD = Number(CFG.freeShippingThreshold) || 0;
  // Envío a domicilio (Lima Metropolitana y Callao): una tarifa en S/ 0 es «Gratis», no «Por
  // coordinar», y si la tarifa llega sin nombre o sin plazo se usan estos.
  const HOME_DELIVERY = Object.assign(
    { ubigeo_prefix: ['1501', '0701'], name: 'Envío a domicilio', description: 'De 1 a 2 días hábiles' },
    CFG.homeDelivery || {}
  );
  const SCHEDULED = Object.assign(
    {
      ubigeo_prefix: ['1501'],
      cutoffHour: 21,
      leadDaysBeforeCutoff: { 0: 2, 1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 2 },
      leadDaysAfterCutoff: { 0: 2, 1: 2, 2: 2, 3: 3, 4: 2, 5: 3, 6: 2 },
      closedWeekdays: [0],
      holidays: [],
      days: 7,
      slots: [
        { value: '9:00 am - 3:00 pm', label: '9:00 a. m. a 3:00 p. m.' },
        { value: '3:00 pm - 9:00 pm', label: '3:00 p. m. a 9:00 p. m.' },
      ],
    },
    CFG.scheduledDelivery || {}
  );
  const PICKUP_STORES = Array.isArray(CFG.pickupStores) ? CFG.pickupStores : [];

  // Tipos de documento: `legacy` es el valor que guardaba /cart en customerData (1 DNI, 2 CE).
  const DEFAULT_DOCUMENTS = {
    person: [
      { label: 'DNI', value: 'dni', legacy: '1', minLen: 8, maxLen: 8, numeric: true },
      { label: 'Carnet de Extranjería', value: 'ce', legacy: '2', minLen: 8, maxLen: 12, numeric: false },
    ],
    company: { label: 'RUC', value: 'ruc', minLen: 11, maxLen: 11, numeric: true },
  };
  const DOCUMENTS =
    CFG.documents && Array.isArray(CFG.documents.person) && CFG.documents.person.length
      ? { person: CFG.documents.person, company: CFG.documents.company || DEFAULT_DOCUMENTS.company }
      : DEFAULT_DOCUMENTS;

  // Región de Shopify por departamento (zonas de Perú, sin el prefijo PE-). Lima Metropolitana
  // (provincia de Lima) es LMA y el Callao es CAL; el resto del departamento de Lima es LIM.
  const PROVINCE_CODES = {
    amazonas: 'AMA',
    ancash: 'ANC',
    apurimac: 'APU',
    arequipa: 'ARE',
    ayacucho: 'AYA',
    cajamarca: 'CAJ',
    callao: 'CAL',
    cusco: 'CUS',
    cuzco: 'CUS',
    huancavelica: 'HUV',
    huanuco: 'HUC',
    ica: 'ICA',
    junin: 'JUN',
    'la libertad': 'LAL',
    lambayeque: 'LAM',
    lima: 'LIM',
    loreto: 'LOR',
    'madre de dios': 'MDD',
    moquegua: 'MOQ',
    pasco: 'PAS',
    piura: 'PIU',
    puno: 'PUN',
    'san martin': 'SAM',
    tacna: 'TAC',
    tumbes: 'TUM',
    ucayali: 'UCA',
  };

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PHONE_PATTERN = /^9\d{8}$/;
  // Claves por línea de la CarrierService: se reemplazan si la línea ya las trae. «Reveni Envíos»
  // lee las de guion bajo (y muestra _dispatch_mode_description bajo el método en el checkout).
  // Las otras son las que pedía Apolo: ya no se mandan, porque el checkout las mostraba bajo cada
  // producto, y si una línea las trae se quitan.
  const LINE_KEYS = [
    'tipo de despacho',
    'shipping method',
    'ubigeo',
    'peso',
    'sku',
    '_dispatch_mode',
    '_dispatch_mode_description',
    '_shipping_method',
    '_ubigeo',
    '_peso',
    '_sku',
  ];
  // Atributos del pedido que puede escribir la hoja: los que no apliquen se vacían en el carrito.
  const ORDER_KEYS = [
    'pais',
    'Modalidad despacho',
    'departamento',
    'provincia',
    'distrito',
    'ubigeo',
    'direccion',
    'referencia',
    'comprobante de pago',
    'email',
    'nombre',
    'apellido',
    'telefono',
    'Documento Identidad',
    'Tipo Documento',
    'tipo de envio',
    'fecha de envio',
    'horario de envio',
    'ruc',
    'razon social',
    'tienda recojo',
  ];

  function normalizeName(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // Deja solo los caracteres que admite el documento (dígitos o alfanuméricos) y su largo máximo.
  // Se aplica en `input` y no en `keypress`: los teclados de Android no disparan keypress.
  function sanitizeDocument(value, doc) {
    if (!doc) return String(value || '');
    const pattern = doc.numeric === false ? /[^a-zA-Z0-9]/g : /[^0-9]/g;
    const clean = String(value || '').replace(pattern, '');
    return doc.maxLen ? clean.slice(0, doc.maxLen) : clean;
  }

  function isDocumentValid(value, doc) {
    if (!doc) return String(value || '').trim() !== '';
    const pattern = doc.numeric === false ? /^[a-zA-Z0-9]+$/ : /^[0-9]+$/;
    return value.length >= doc.minLen && value.length <= doc.maxLen && pattern.test(value);
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
  }

  // Ubigeos del INEI (assets/enova-ubigeo-pe.js, ~57 KB): se cargan la primera vez que se elige
  // envío a domicilio, no en cada página. Si falla, se reintenta en la siguiente.
  let ubigeoRequest = null;

  function loadUbigeo() {
    if (Array.isArray(window.EnovaUbigeoPE)) return Promise.resolve(window.EnovaUbigeoPE);
    if (!ubigeoRequest) {
      ubigeoRequest = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = CFG.ubigeoUrl;
        script.async = true;
        script.onload = () => (Array.isArray(window.EnovaUbigeoPE) ? resolve(window.EnovaUbigeoPE) : reject(new Error('ubigeo')));
        script.onerror = () => reject(new Error('ubigeo'));
        document.head.appendChild(script);
      }).catch((error) => {
        ubigeoRequest = null;
        throw error;
      });
    }
    return ubigeoRequest;
  }

  // Mismo nombre sin tildes ni mayúsculas: los datos guardados por /cart vienen de Apolo
  // («Jesus Maria», «San Juan De Lurigancho»).
  function sameName(a, b) {
    return !!a && !!b && normalizeName(a) === normalizeName(b);
  }

  // Lima primero en departamento y provincia (de ahí son la mayoría de pedidos); el resto, en orden
  // alfabético.
  function limaFirst(items) {
    const index = items.findIndex((item) => sameName(item.name, 'Lima'));
    return index > 0 ? [items[index]].concat(items.slice(0, index), items.slice(index + 1)) : items;
  }

  // Lo guardado por /cart con los nombres del INEI: Apolo ponía el Callao como provincia de Lima
  // y escribía «Cuzco».
  function migrateSavedPlace(place) {
    const result = Object.assign({}, place);
    if (sameName(result.region, 'Lima') && sameName(result.province, 'Callao')) result.region = 'Callao';
    ['region', 'province', 'district'].forEach((level) => {
      if (sameName(result[level], 'Cuzco')) result[level] = 'Cusco';
    });
    return result;
  }

  // Tarifas que devuelve el Ajax API (`async_shipping_rates`): null mientras Shopify calcula.
  function parseShopifyRates(body) {
    const list = body && body.shipping_rates;
    if (!Array.isArray(list)) return null;
    return list
      .map((rate) => {
        const name = String(rate.presentment_name || rate.name || '').trim();
        return {
          code: String(rate.code || name).trim(),
          name,
          description: String(rate.description || '').trim(),
          cents: Math.round(Number(rate.price) * 100),
        };
      })
      .filter((rate) => rate.name && rate.code && Number.isFinite(rate.cents) && rate.cents >= 0);
  }

  // Las tarifas del distrito para el carrito, las mismas que dará el checkout: Lima y Callao las
  // cotiza «Reveni Envíos» con el ubigeo como código postal; provincias, la tarifa nativa de
  // Shopify. Se calculan aparte: se pregunta enseguida y luego cada medio segundo, hasta 10 s.
  function shopifyRates(address) {
    const query = new URLSearchParams({
      'shipping_address[country]': COUNTRY,
      'shipping_address[province]': address.provinceCode,
      'shipping_address[city]': address.city,
      'shipping_address[zip]': address.zip,
    }).toString();
    const poll = (attempt) =>
      new Promise((resolve) => setTimeout(resolve, attempt ? 500 : 150))
        .then(() => getJSON(`${root()}cart/async_shipping_rates.json?${query}`))
        .then((body) => {
          const rates = parseShopifyRates(body);
          if (rates) return rates;
          if (attempt >= 19) throw new Error('timeout');
          return poll(attempt + 1);
        });
    return fetch(`${root()}cart/prepare_shipping_rates.json?${query}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return poll(0);
    });
  }

  function storefront(query, variables) {
    return fetch(STOREFRONT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query, variables }),
    }).then((response) => response.json());
  }

  function readCustomerData() {
    try {
      return JSON.parse(window.localStorage.getItem(CUSTOMER_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeCustomerData(values) {
    try {
      const data = Object.assign(readCustomerData(), values);
      window.localStorage.setItem(CUSTOMER_KEY, JSON.stringify(data));
    } catch (error) {
      /* sin storage: el flujo sigue igual */
    }
  }

  // El mini-cart puede abrirse en cualquier página: los UTMs se capturan al cargar cada una.
  function captureUTMs() {
    try {
      const params = new URLSearchParams(window.location.search);
      const utms = {};
      UTM_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) utms[key] = value;
      });
      if (Object.keys(utms).length) sessionStorage.setItem('utm_params', JSON.stringify(utms));
    } catch (error) {
      /* noop */
    }
  }

  function appendUTMs(url) {
    let utms = {};
    try {
      utms = JSON.parse(sessionStorage.getItem('utm_params')) || {};
    } catch (error) {
      utms = {};
    }
    const params = Object.keys(utms).map((key) => `${key}=${encodeURIComponent(utms[key])}`);
    if (!params.length) return url;
    return url + (url.includes('?') ? '&' : '?') + params.join('&');
  }

  // Igual que /cart: el checkout de la Storefront API se abre con _fd=0.
  function withCheckoutParams(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('_fd', '0');
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  function getAppliedDiscountCode(cart) {
    const apps = (cart && cart.cart_level_discount_applications) || [];
    const byCode = apps.find((app) => app && app.type === 'discount_code' && app.title);
    if (byCode) return byCode.title;
    const codes = (cart && cart.discount_codes) || [];
    const applied = codes.find((code) => code && code.applicable !== false) || codes[0];
    return applied ? applied.code : null;
  }

  function provinceCodeFor({ ubigeo, region, province }) {
    const code = String(ubigeo || '');
    if (code.startsWith('1501') || (normalizeName(region) === 'lima' && normalizeName(province) === 'lima')) return 'LMA';
    if (code.startsWith('0701') || normalizeName(province) === 'callao') return 'CAL';
    return PROVINCE_CODES[normalizeName(region)] || '';
  }

  function toE164(phone) {
    const digits = String(phone || '').replace(/\D/g, '');
    return digits ? `+51${digits}` : '';
  }

  function compact(object) {
    const result = {};
    Object.keys(object).forEach((key) => {
      const value = object[key];
      if (value != null && String(value).trim() !== '') result[key] = typeof value === 'string' ? value.trim() : value;
    });
    return result;
  }

  // Dirección para Shopify con el mismo mapeo que /cart: dirección y referencia en address1,
  // distrito en address2 y provincia en city (así se leen en las guías de despacho).
  function buildShippingAddress(data) {
    let address1 = data.address || '';
    if (data.reference && address1 && !address1.includes(data.reference)) address1 = `${address1}, ${data.reference}`;
    else if (!address1 && data.reference) address1 = data.reference;
    return compact({
      firstName: data.firstName,
      lastName: data.lastName,
      address1,
      address2: data.district,
      city: data.province || data.district,
      company: data.company,
      provinceCode: provinceCodeFor(data),
      countryCode: COUNTRY,
      phone: toE164(data.phone),
    });
  }

  // Dirección por la URL del checkout (kind: billing_address o shipping_address). La Cart API no
  // tiene billingAddress, y en retiro una dirección de envío en el carrito haría abrir el checkout
  // en «Envío»: por la URL, el checkout la deja en sus campos sin cambiar de modo.
  function appendAddress(url, kind, address) {
    if (!address) return url;
    const fields = {
      first_name: address.firstName,
      last_name: address.lastName,
      company: address.company,
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      province: address.provinceCode ? `PE-${address.provinceCode}` : '',
      phone: address.phone,
    };
    const params = Object.keys(fields)
      .filter((key) => fields[key])
      .map((key) => `checkout[${kind}][${key}]=${encodeURIComponent(fields[key])}`);
    if (!params.length) return url;
    params.push(`checkout[${kind}][country]=${COUNTRY}`);
    return url + (url.includes('?') ? '&' : '?') + params.join('&');
  }

  // Respaldo si la Storefront API no responde: checkout nativo con email y dirección en la URL
  // (lo que hacía /cart en el mismo caso).
  function nativeCheckoutUrl(order) {
    const params = new URLSearchParams();
    if (order.email) params.set('checkout[email]', order.email);
    const address = order.shippingAddress;
    if (address) {
      const fields = {
        first_name: address.firstName,
        last_name: address.lastName,
        address1: address.address1,
        address2: address.address2,
        city: address.city,
        province: address.provinceCode ? `PE-${address.provinceCode}` : '',
        country: COUNTRY,
        phone: address.phone,
        company: address.company,
      };
      Object.keys(fields).forEach((key) => {
        if (fields[key]) params.set(`checkout[shipping_address][${key}]`, fields[key]);
      });
    }
    const query = params.toString();
    const url = `${root()}checkout${query ? `?${query}` : ''}`;
    return appendAddress(url, 'billing_address', order.shippingAddress ? null : order.billingAddress);
  }

  function matchesUbigeo(prefix, ubigeo) {
    const prefixes = Array.isArray(prefix) ? prefix : [prefix];
    return prefixes.some((item) => item === '*' || String(ubigeo).startsWith(String(item)));
  }

  // Formato de moneda de la tienda (MinimogSettings.money_format, p. ej. "S/{{amount}}"),
  // para que coincida con los precios del carrito. Recibe céntimos.
  function formatMoney(cents) {
    const format = (window.MinimogSettings && window.MinimogSettings.money_format) || 'S/{{amount}}';
    const value = (Number(cents) || 0) / 100;
    const group = (number, decimals, thousands, decimal) => {
      const [integer, fraction] = number.toFixed(decimals).split('.');
      const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${withThousands}${decimal}${fraction}` : withThousands;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  }

  /* ---------------- Fechas del envío programado (hora de Lima) ---------------- */

  // Fecha y hora actuales en Lima (UTC-5, sin horario de verano), sin depender de la zona
  // horaria del navegador.
  function limaNow() {
    const parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Lima',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23',
    })
      .formatToParts(new Date())
      .forEach(({ type, value }) => (parts[type] = value));
    return {
      date: new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))),
      hour: Number(parts.hour) % 24,
    };
  }

  const DATE_LABEL = new Intl.DateTimeFormat('es-PE', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' });

  // Próximas fechas de entrega: mismas reglas que tenía /cart (días de anticipación según el día
  // y si ya pasó la hora de corte; sin domingos ni feriados).
  function scheduleDates() {
    const now = limaNow();
    const lead = now.hour >= Number(SCHEDULED.cutoffHour) ? SCHEDULED.leadDaysAfterCutoff : SCHEDULED.leadDaysBeforeCutoff;
    const cursor = new Date(now.date);
    cursor.setUTCDate(cursor.getUTCDate() + (Number(lead[cursor.getUTCDay()]) || 1));
    const closed = (SCHEDULED.closedWeekdays || []).map(Number);
    const holidays = SCHEDULED.holidays || [];
    const dates = [];
    for (let guard = 0; dates.length < Number(SCHEDULED.days || 7) && guard < 90; guard += 1) {
      const iso = cursor.toISOString().slice(0, 10);
      if (!closed.includes(cursor.getUTCDay()) && !holidays.includes(iso)) {
        const [year, month, day] = iso.split('-');
        const label = DATE_LABEL.format(cursor);
        dates.push({ value: `${day}-${month}-${year}`, label: label.charAt(0).toUpperCase() + label.slice(1) });
      }
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return dates;
  }

  const CART_CREATE = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { id checkoutUrl }
        userErrors { code field message }
      }
    }
  `;

  class EnovaMinicartCheckout {
    constructor(drawer) {
      this.drawer = drawer;
      this.sheet = drawer.querySelector('[data-enova-checkout-sheet]');
      this.form = this.sheet.querySelector('[data-enova-checkout-form]');
      this.modesEl = this.sheet.querySelector('[data-enova-checkout-modes]');
      this.panels = {
        delivery: this.sheet.querySelector('[data-enova-checkout-panel="delivery"]'),
        pickup: this.sheet.querySelector('[data-enova-checkout-panel="pickup"]'),
      };
      this.selects = {
        region: this.sheet.querySelector('[data-enova-ubigeo="region"]'),
        province: this.sheet.querySelector('[data-enova-ubigeo="province"]'),
        district: this.sheet.querySelector('[data-enova-ubigeo="district"]'),
      };
      this.addressFields = {
        address: this.sheet.querySelector('[data-enova-address="address"]'),
        reference: this.sheet.querySelector('[data-enova-address="reference"]'),
      };
      this.typesEl = this.sheet.querySelector('[data-enova-shipping-types]');
      this.regularMetaEl = this.sheet.querySelector('[data-enova-regular-meta]');
      this.scheduleSelects = {
        date: this.sheet.querySelector('[data-enova-schedule="date"]'),
        slot: this.sheet.querySelector('[data-enova-schedule="slot"]'),
      };
      this.shippingEl = this.sheet.querySelector('[data-enova-checkout-shipping]');
      this.storesEl = this.sheet.querySelector('[data-enova-checkout-stores]');
      this.pickupSummaryEl = this.sheet.querySelector('[data-enova-pickup-summary]');
      this.summaryEl = this.sheet.querySelector('[data-enova-checkout-summary]');
      this.collapses = {};
      this.sheet.querySelectorAll('[data-enova-collapse]').forEach((el) => {
        this.collapses[el.dataset.enovaCollapse] = el;
      });
      this.subtitleEl = this.sheet.querySelector('.enova-minicart-sheet__subtitle');
      this.commonEl = this.sheet.querySelector('[data-enova-checkout-common]');
      this.contactFields = {
        firstName: this.sheet.querySelector('[data-enova-contact="firstName"]'),
        lastName: this.sheet.querySelector('[data-enova-contact="lastName"]'),
        email: this.sheet.querySelector('[data-enova-contact="email"]'),
        phone: this.sheet.querySelector('[data-enova-contact="phone"]'),
      };
      this.documentEl = this.sheet.querySelector('[data-enova-checkout-document]');
      this.documentFields = {
        type: this.documentEl.querySelector('[data-enova-document="type"]'),
        number: this.documentEl.querySelector('[data-enova-document="number"]'),
        invoice: this.documentEl.querySelector('[data-enova-document="invoice"]'),
        companyField: this.documentEl.querySelector('[data-enova-document="company-field"]'),
        companyLabel: this.documentEl.querySelector('[data-enova-document="company-label"]'),
        company: this.documentEl.querySelector('[data-enova-document="company"]'),
      };
      this.errorEl = this.sheet.querySelector('[data-enova-checkout-error]');
      this.submitBtn = this.sheet.querySelector('[data-enova-checkout-submit]');
      this.submitLabel = this.sheet.querySelector('[data-enova-checkout-submit-label]');
      this.msg = this.sheet.dataset;

      this.mode = null;
      this.methods = [];
      this.stores = [];
      this.documents = DOCUMENTS;
      this.shippingType = 'regular';
      this.scheduleEligible = false;
      this.ratesRequest = 0;
      this.initialized = false;
      // Secciones mostradas como resumen (datos guardados). Al editarlas se abren y quedan así.
      this.compact = { delivery: false, contact: false, document: false };
      this.ubigeoLoaded = false;
      this.storesLoaded = false;

      this.onKeydown = this.onKeydown.bind(this);
      drawer.addEventListener('click', this.onDrawerClick.bind(this), true);
      this.sheet.addEventListener('click', this.onSheetClick.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.modesEl.addEventListener('change', (event) => {
        if (event.target.name === 'enova-minicart-mode') this.setMode(event.target.value);
      });
      this.selects.region.addEventListener('change', () => this.onRegionChange());
      this.selects.province.addEventListener('change', () => this.onProvinceChange());
      this.selects.district.addEventListener('change', () => this.onDistrictChange());
      Object.values(this.addressFields).forEach((input) => {
        input.addEventListener('input', () => {
          input.classList.remove('has-error');
          this.renderSaveLinks();
        });
      });
      this.typesEl.addEventListener('change', (event) => {
        if (event.target.name === 'enova-minicart-shipping-type') this.setShippingType(event.target.value, { focus: true });
      });
      Object.values(this.scheduleSelects).forEach((select) => {
        select.addEventListener('change', () => {
          select.classList.remove('has-error');
          this.clearError();
          this.renderTotal();
          this.renderSaveLinks();
        });
      });
      this.shippingEl.addEventListener('change', () => {
        this.clearError();
        this.renderTotal();
      });
      this.storesEl.addEventListener('change', () => {
        this.clearError();
        this.renderPickupSummary();
        this.renderSaveLinks();
      });
      Object.keys(this.contactFields).forEach((key) => {
        const input = this.contactFields[key];
        input.addEventListener('input', () => {
          input.classList.remove('has-error');
          if (key === 'phone') {
            const clean = input.value.replace(/\D/g, '').slice(0, 9);
            if (clean !== input.value) input.value = clean;
          }
          this.renderSaveLinks();
        });
      });
      // Desde que el cliente toca el bloque, lo suyo manda sobre lo guardado.
      this.documentEl.addEventListener('input', () => (this.documentTouched = true), true);
      this.documentEl.addEventListener('change', () => (this.documentTouched = true), true);
      this.documentFields.type.addEventListener('change', () => {
        this.documentFields.type.classList.remove('has-error');
        this.documentFields.number.classList.remove('has-error');
        this.applyDocumentType();
      });
      this.documentFields.number.addEventListener('input', () => {
        this.documentFields.number.classList.remove('has-error');
        this.sanitizeField(this.documentFields.number, this.selectedDocument());
        this.renderDocumentSave();
      });
      this.documentFields.company.addEventListener('input', () => {
        this.documentFields.company.classList.remove('has-error');
        this.sanitizeField(this.documentFields.company, this.documents.company);
        this.renderDocumentSave();
      });
      this.documentFields.invoice.addEventListener('change', () => {
        this.documentFields.company.classList.remove('has-error');
        this.toggleInvoice(this.documentFields.invoice.checked, { focus: true });
      });
      this.watchHeight();
      this.watchScrollArea();

      // Al volver del checkout con «atrás» (o el gesto de swipe en iOS/Android) la página
      // se restaura desde el bfcache tal cual se dejó: con el botón en «Procesando…».
      window.addEventListener('pageshow', (event) => {
        if (event.persisted || this.submitting) this.setSubmitting(false);
      });

      // Si se cierra el drawer, la hoja se cierra con él.
      new MutationObserver(() => {
        if (!drawer.classList.contains('is-open') && this.isOpen) this.close({ restoreFocus: false });
        // Abrir el carrito (p. ej. al agregar un producto) también prepara la hoja.
        if (drawer.classList.contains('is-open')) this.prefetch();
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      this.schedulePrefetch();
    }

    // Precarga: con el navegador libre tras cargar la página se arma la hoja en segundo plano
    // (ubigeo o tienda guardados, tarifa y documento), así al abrirla ya está lista.
    schedulePrefetch() {
      const run = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(() => this.prefetch(), { timeout: 3000 });
        else setTimeout(() => this.prefetch(), 1200);
      };
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run, { once: true });
    }

    prefetch() {
      if (this.initialized) return;
      // Sin productos no hay a dónde ir a pagar: no se consulta nada.
      const summary = this.readDrawerSummary();
      if (!summary || !summary.count) return;
      this.cartSummary = summary;
      this.initialized = true;
      this.init();
    }

    // Firma del carrito para saber si la tarifa precargada sigue vigente.
    cartSignature(summary) {
      return summary ? `${summary.count}|${summary.total}|${summary.original}` : '';
    }

    get isOpen() {
      return this.sheet.classList.contains('is-open');
    }

    // Capa de GPU para el panel solo mientras sube/baja (se crea antes del primer frame
    // y se libera al terminar, para no retener memoria).
    markSliding() {
      clearTimeout(this.slideTimer);
      this.sheet.classList.add('is-sliding');
      this.slideTimer = setTimeout(() => this.sheet.classList.remove('is-sliding'), 500);
    }

    // Anima la altura del panel cuando cambia su contenido (paso 2, tarifa, tiendas…).
    // ResizeObserver corre tras el layout y antes del pintado: se fija la altura anterior,
    // y en el siguiente frame la nueva, así el salto nunca llega a verse.
    watchHeight() {
      const panel = this.sheet.querySelector('.enova-minicart-sheet__panel');
      const head = this.sheet.querySelector('.enova-minicart-sheet__head');
      const inner = this.sheet.querySelector('[data-enova-checkout-inner]');
      const actions = this.sheet.querySelector('[data-enova-checkout-actions]');
      if (!panel || !head || !inner || !actions || typeof ResizeObserver === 'undefined') return;

      // Altura natural = encabezado + contenido del cuerpo + acciones (sin depender del
      // alto actual del panel, que es justo lo que se anima).
      const naturalHeight = () => head.offsetHeight + inner.offsetHeight + actions.offsetHeight;

      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
      let lastHeight = 0;
      let frame = 0;
      let restTop = null;
      let glideTimer = 0;

      // El botón nunca salta: si un cambio de contenido o el fin de la animación lo mueve de
      // lugar, el pie (resumen + botón) se desliza desde donde estaba (FLIP con transform, antes
      // del pintado). La referencia es el botón: si el resumen cambia de alto, se absorbe arriba.
      const button = actions.querySelector('[data-enova-checkout-submit]') || actions;
      const buttonTop = () => button.getBoundingClientRect().top;
      const glide = (fromTop) => {
        const layoutTop = buttonTop() - (actions.enovaGlideOffset || 0);
        if (fromTop == null || (reduceMotion && reduceMotion.matches)) return layoutTop;
        const delta = fromTop - layoutTop;
        if (Math.abs(delta) < 1) return layoutTop;
        actions.enovaGlideOffset = delta;
        actions.style.transition = 'none';
        actions.style.transform = `translate3d(0, ${delta}px, 0)`;
        requestAnimationFrame(() => {
          actions.enovaGlideOffset = 0;
          actions.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          actions.style.transform = '';
        });
        clearTimeout(glideTimer);
        glideTimer = setTimeout(() => (actions.style.transition = ''), 340);
        return layoutTop;
      };

      const finish = (event) => {
        if (event && (event.target !== panel || event.propertyName !== 'height')) return;
        const visibleTop = buttonTop();
        panel.style.height = '';
        panel.classList.remove('is-resizing', 'is-pinned');
        panel.style.removeProperty('will-change');
        restTop = this.isOpen ? glide(visibleTop) : null;
      };
      panel.addEventListener('transitionend', finish);

      const observer = new ResizeObserver(() => {
        const height = naturalHeight();
        const previous = lastHeight;
        lastHeight = height;
        if (!previous || previous === height || !this.isOpen || (reduceMotion && reduceMotion.matches)) {
          restTop = this.isOpen ? buttonTop() : null;
          return;
        }
        // Un bloque que se despliega solo (p. ej. el RUC) ya anima su alto: la hoja lo sigue tal cual.
        if (performance.now() < this.heightFrozenUntil) {
          cancelAnimationFrame(frame);
          finish();
          return;
        }

        cancelAnimationFrame(frame);
        // Si ya había una animación en curso, parte desde la altura visible actual.
        const from = panel.style.height ? panel.getBoundingClientRect().height : previous;
        // Durante la animación el botón se mantiene abajo; al terminar, si su lugar en reposo es
        // otro (contenido más alto que la pantalla), se desliza hasta ahí.
        panel.classList.add('is-resizing', 'is-pinned');
        panel.style.willChange = this.sheet.classList.contains('is-sliding') ? 'transform, height' : 'height';
        panel.style.height = `${from}px`;
        restTop = glide(restTop);
        frame = requestAnimationFrame(() => {
          panel.style.height = `${height}px`;
          // Si la altura queda topada por max-height no hay transitionend: se limpia igual.
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(finish, 450);
        });
      });
      [head, inner, actions].forEach((node) => observer.observe(node));
    }

    // Pausa la animación de alto de la hoja mientras un bloque anima el suyo.
    freezeHeightAnimation(duration) {
      this.heightFrozenUntil = performance.now() + duration;
    }

    // En desktop el pie (resumen + «Ir a pagar») queda fijo sobre el contenido, que hace scroll
    // detrás y sin barra. Se mide el pie para que lo enfocado o recién abierto quede por encima de
    // él (scroll-padding) y se marca cuando hay más contenido abajo (sombra del pie).
    watchScrollArea() {
      const body = this.sheet.querySelector('.enova-minicart-sheet__body');
      const inner = this.sheet.querySelector('[data-enova-checkout-inner]');
      const actions = this.sheet.querySelector('[data-enova-checkout-actions]');
      if (!body || !inner || !actions) return;
      this.bodyEl = body;
      const desktop = window.matchMedia('(min-width: 768px)');
      const sync = () => {
        const pinned = desktop.matches;
        body.style.setProperty('--mc-actions-h', pinned ? `${actions.offsetHeight}px` : '0px');
        // El aire final del contenido (padding-bottom) no cuenta como «más contenido».
        const trailing = parseFloat(getComputedStyle(inner).paddingBottom) || 0;
        const below = body.scrollHeight - body.clientHeight - body.scrollTop > trailing + 2;
        this.sheet.classList.toggle('has-more-below', pinned && below);
      };
      body.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(sync);
        [body, inner, actions].forEach((node) => observer.observe(node));
      }
      sync();
    }

    // Lleva a la vista lo que se acaba de abrir (fecha y horario, «Guardar», RUC) cuando termina
    // de desplegarse, por encima del pie fijo.
    reveal(el, delay = 320) {
      if (!el || !this.isOpen) return;
      clearTimeout(this.revealTimer);
      this.revealTimer = setTimeout(() => {
        if (!this.isOpen || el.closest('[hidden]') || !el.classList.contains('is-open')) return;
        const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
      }, delay);
    }

    onDrawerClick(event) {
      const trigger = event.target.closest('[data-enova-minicart-checkout]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      this.trigger = trigger;
      this.open();
    }

    onSheetClick(event) {
      const save = event.target.closest('[data-enova-save]');
      if (save) {
        event.preventDefault();
        this.save(save.dataset.enovaSave);
        return;
      }
      const summaryRow = event.target.closest('[data-enova-summary]');
      if (summaryRow) {
        event.preventDefault();
        this.expand(summaryRow.dataset.enovaSummary, { focus: true });
        return;
      }
      if (event.target.closest('[data-enova-checkout-switch-delivery]')) {
        event.preventDefault();
        this.setMode('delivery');
        return;
      }
      if (event.target.closest('[data-enova-checkout-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
      }
    }

    open() {
      this.sheet.removeAttribute('inert');
      this.sheet.setAttribute('aria-hidden', 'false');
      this.drawer.classList.add('has-checkout-sheet');
      this.markSliding();
      requestAnimationFrame(() => this.sheet.classList.add('is-open'));
      document.addEventListener('keydown', this.onKeydown, true);

      this.clearError();
      this.setSubmitting(false);
      // Montos del carrito al instante desde el pie del mini-cart (se re-renderiza con cada
      // cambio): así el resumen nace completo y no le aparecen líneas después.
      const drawerSummary = this.readDrawerSummary();
      if (drawerSummary) this.cartSummary = drawerSummary;
      if (!this.initialized) {
        this.initialized = true;
        this.init();
      }
      // El carrito pudo cambiar: se recalcula la tarifa o se revalida el stock al reabrir.
      if (this.mode === 'delivery') {
        // Si la tarifa ya se calculó (o se está calculando) para este distrito y este mismo
        // carrito, se reutiliza: la hoja abre con el resumen completo, sin skeleton.
        const district = this.selectedOption('district');
        const sameDistrict = !!district && district.dataset.code === this.ratesUbigeo;
        const fresh =
          sameDistrict &&
          (this.ratesInFlight || (this.methods.length && this.ratesCartSig === this.cartSignature(drawerSummary)));
        if (!fresh) this.onDistrictChange();
        if (this.shippingPlaceholder) this.refreshCartSummary();
      }
      if (this.mode === 'pickup') this.renderPickupSummary();
      if (this.mode === 'pickup' && this.storesLoaded) this.loadStores();
      // Las fechas del envío programado dependen del día y la hora: se rehacen al reabrir.
      if (this.shippingType === 'scheduled') this.renderScheduleOptions();

      setTimeout(() => {
        const allSaved = this.compact.delivery && this.compact.contact && this.compact.document;
        const target = allSaved
          ? this.submitBtn
          : this.mode
            ? this.form.querySelector('input:checked, select:not(:disabled)')
            : this.modesEl.querySelector('input');
        if (target) target.focus({ preventScroll: true });
      }, 320);
    }

    close({ restoreFocus = true } = {}) {
      this.markSliding();
      this.sheet.classList.remove('is-open');
      this.sheet.setAttribute('aria-hidden', 'true');
      this.sheet.setAttribute('inert', '');
      this.drawer.classList.remove('has-checkout-sheet');
      document.removeEventListener('keydown', this.onKeydown, true);
      if (!restoreFocus) return;
      const trigger =
        this.trigger && document.contains(this.trigger)
          ? this.trigger
          : this.drawer.querySelector('[data-enova-minicart-checkout]');
      if (trigger) trigger.focus({ preventScroll: true });
    }

    init() {
      const saved = (this.saved = readCustomerData());
      this.savedUbigeo = migrateSavedPlace({ region: saved.region, province: saved.city, district: saved.locality });
      this.restoreContact();
      this.restoreAddress();
      this.restoreDocument();

      const savedMode =
        saved.shippingMethod === 'recojo-tienda' ? 'pickup' : saved.shippingMethod === 'delivery' ? 'delivery' : null;
      // La fecha del envío programado no se guarda (vence): quien lo usó la vuelve a elegir.
      const savedScheduled = saved.shippingMethodType === 'programmed';
      // Quien ya tiene datos guardados ve un resumen en vez del formulario completo.
      this.compact.delivery =
        savedMode === 'delivery'
          ? !!(saved.region && saved.city && saved.locality && saved.address && saved.reference) && !savedScheduled
          : savedMode === 'pickup' && !!saved.pickupStore;
      this.compact.contact = this.compact.delivery && !!this.readContact({ mark: false });
      this.compact.document = this.compact.delivery && !!this.readDocument({ mark: false });
      if (savedScheduled) this.shippingType = 'scheduled';
      if (savedMode) this.setMode(savedMode);
      this.applyCompact();
      this.renderSaveLinks();
    }

    /* ---------------- Resumen compacto ---------------- */

    // Abre o cierra un plegable. Con la hoja abierta se anima (y la hoja sigue su alto cuadro
    // a cuadro); cerrada o al restaurar, cambia directo.
    setCollapse(el, open, animate) {
      if (!el || el.classList.contains('is-open') === open) return;
      if (!animate) {
        el.classList.add('is-instant');
        requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove('is-instant')));
      }
      el.classList.toggle('is-open', open);
      el.inert = !open;
      el.setAttribute('aria-hidden', open ? 'false' : 'true');
    }

    canAnimate() {
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      return this.isOpen && !reduceMotion;
    }

    // Filas del resumen ↔ formulario: lo resumido se pliega y su fila se despliega a la vez.
    applyCompact() {
      const { delivery, contact, document } = this.compact;
      const animate = this.canAnimate();
      if (animate) this.freezeHeightAnimation(380);
      const states = {
        review: delivery || contact || document,
        'summary-delivery': delivery,
        'summary-contact': contact,
        'summary-document': document,
        modes: !delivery,
        ubigeo: !delivery,
        stores: !delivery,
        contact: !contact,
        document: !document,
      };
      Object.keys(states).forEach((key) => this.setCollapse(this.collapses[key], states[key], animate));
      if (this.subtitleEl) {
        this.subtitleEl.textContent = delivery && contact && document ? this.msg.msgSubtitleReview : this.msg.msgSubtitle;
      }
      this.renderSummary();
    }

    // Abre una sección del resumen para editarla (o porque lo guardado ya no sirve).
    expand(section, { focus = false } = {}) {
      if (!this.compact[section]) return;
      this.compact[section] = false;
      this.applyCompact();
      if (!focus) return;
      const targets = {
        delivery: this.modesEl.querySelector('input:checked') || this.modesEl.querySelector('input'),
        contact: this.contactFields.firstName,
        document: this.documentFields.number,
      };
      const target = targets[section];
      // Foco al terminar de abrirse. En pantallas táctiles no se fuerza el teclado.
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (!target || (coarse && section !== 'delivery')) return;
      setTimeout(() => target.focus({ preventScroll: true }), this.canAnimate() ? 300 : 0);
    }

    collapse(section) {
      if (this.compact[section]) return;
      this.compact[section] = true;
      this.applyCompact();
    }

    // «Guardar» aparece bajo cada bloque cuando está completo; al tocarlo, el bloque se pliega
    // en su fila del resumen.
    renderSaveLinks() {
      const animate = this.canAnimate();
      if (animate) this.freezeHeightAnimation(360);
      const store = this.selectedStore();
      const states = {
        'save-delivery': this.mode === 'delivery' && !!this.readDelivery({ mark: false }),
        'save-pickup': this.mode === 'pickup' && !!(store && store.availability.selectable),
        'save-contact': !!this.readContact({ mark: false }),
      };
      Object.keys(states).forEach((key) => {
        const el = this.collapses[key];
        const appearing = !!el && states[key] && !el.classList.contains('is-open');
        this.setCollapse(el, states[key], animate);
        // «Guardar» recién aparecido: se lleva a la vista para que no quede tapado por el pie.
        if (appearing && animate) this.reveal(el);
      });
    }

    save(section) {
      if (section === 'document') {
        this.saveDocument();
        return;
      }
      if (section === 'contact') {
        const contact = this.readContact();
        if (!contact) {
          this.showError(this.contactMessage);
          return;
        }
        writeCustomerData(this.contactSaved(contact));
      } else {
        const delivery = this.mode === 'pickup' ? this.selectedStore() : this.readDelivery();
        if (!delivery) {
          if (this.deliveryMessage) this.showError(this.deliveryMessage);
          return;
        }
        writeCustomerData(this.mode === 'pickup' ? this.pickupSaved(delivery) : this.deliverySaved(delivery));
      }
      const key = section === 'contact' ? 'contact' : 'delivery';
      this.collapse(key);
      // El foco pasa a «Cambiar» de la fila recién desplegada (el enlace quedó plegado).
      const row = this.summaryEl.querySelector(`[data-enova-summary="${key}"]`);
      setTimeout(() => row.focus({ preventScroll: true }), this.canAnimate() ? 300 : 0);
    }

    // «Guardar» dentro del campo: sin factura, en el del documento cuando está completo; con
    // factura, en el del RUC cuando documento y RUC están completos.
    renderDocumentSave() {
      const doc = this.selectedDocument();
      const number = this.documentFields.number.value.trim();
      const needsInvoice = this.documentFields.invoice.checked;
      const company = this.documents.company;
      const numberOk = isDocumentValid(number, doc);
      const rucOk = isDocumentValid(this.documentFields.company.value.trim(), company);
      this.toggleSave('number', numberOk && !needsInvoice);
      this.toggleSave('company', numberOk && needsInvoice && rucOk);
    }

    toggleSave(field, visible) {
      const wrap = this.documentEl.querySelector(`[data-enova-save-wrap="${field}"]`);
      if (!wrap) return;
      const button = wrap.querySelector('[data-enova-save]');
      wrap.classList.toggle('has-save', visible);
      button.tabIndex = visible ? 0 : -1;
      button.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    saveDocument() {
      const documentData = this.readDocument();
      if (!documentData) {
        this.showError(this.documentMessage);
        return;
      }
      writeCustomerData(documentData.saved);
      this.collapse('document');
      const row = this.summaryEl.querySelector('[data-enova-summary="document"]');
      setTimeout(() => row.focus({ preventScroll: true }), this.canAnimate() ? 300 : 0);
    }

    setSummaryRow(section, title, detail) {
      const row = this.summaryEl.querySelector(`[data-enova-summary="${section}"]`);
      row.querySelector('[data-enova-summary-title]').textContent = title;
      row.querySelector('[data-enova-summary-detail]').textContent = detail;
      row.setAttribute(
        'aria-label',
        `${title}. ${String(detail).replace(/\n/g, ', ')}. ${row.querySelector('.enova-minicart-review__edit').textContent}`
      );
    }

    renderSummary() {
      if (this.compact.delivery && this.mode) {
        const modeTitle = this.modesEl.querySelector(`[data-enova-checkout-mode="${this.mode}"] .enova-minicart-mode__title`);
        let detail;
        // Una línea por dato (dirección, distrito, fecha…): en la fila angosta no se parten a la mitad.
        if (this.mode === 'pickup') {
          const store = this.selectedStore() || this.savedStore();
          detail = store ? [store.name, store.address].filter(Boolean).join('\n') : '';
        } else {
          const pick = (level) => (this.selectedOption(level) || {}).value || this.savedUbigeo[level] || '';
          const place = [pick('district'), pick('province')].filter(Boolean).join(', ');
          const parts = [this.addressFields.address.value.trim(), place];
          const schedule = this.readSchedule();
          if (this.shippingType === 'scheduled' && this.scheduleEligible && schedule) parts.push(this.scheduleText(schedule));
          detail = parts.filter(Boolean).join('\n');
        }
        this.setSummaryRow('delivery', modeTitle ? modeTitle.textContent.trim() : '', detail);
      }
      if (this.compact.contact) {
        const contact = this.readContact({ mark: false }) || {};
        this.setSummaryRow(
          'contact',
          [contact.firstName, contact.lastName].filter(Boolean).join(' '),
          [contact.email, contact.phone ? `+51 ${contact.phone}` : ''].filter(Boolean).join('\n')
        );
      }
      if (this.compact.document) {
        const doc = this.selectedDocument();
        const person = `${doc.label} ${this.documentFields.number.value.trim()}`;
        const invoice = this.documentFields.invoice.checked;
        const company = this.documents.company ? this.documents.company.label : 'RUC';
        this.setSummaryRow(
          'document',
          invoice ? this.msg.msgReceiptFactura : this.msg.msgReceiptBoleta,
          invoice ? `${company} ${this.documentFields.company.value.trim()} · ${person}` : person
        );
      }
    }

    setMode(mode) {
      const previous = this.mode;
      this.mode = mode;
      // El resumen describe el modo guardado: si se elige otro, se muestra el formulario.
      if (!mode || (previous && previous !== mode)) this.expand('delivery');
      this.clearError();
      this.modesEl.querySelectorAll('input[name="enova-minicart-mode"]').forEach((input) => {
        input.checked = input.value === mode;
        input.closest('.enova-minicart-mode').classList.toggle('is-active', input.value === mode);
      });
      // Alto del panel que sale: el que entra lo conserva mientras carga su contenido, así la
      // hoja no baja para luego volver a subir (una sola transición, al alto real).
      const outgoing = previous && previous !== mode ? this.panels[previous] : null;
      const heldHeight = outgoing && !outgoing.hidden && this.isOpen ? outgoing.offsetHeight : 0;
      // Tus datos y comprobante se mueven recién al intercambiar los paneles: así se desvanecen
      // con el panel que sale y aparecen con el que entra.
      this.switchPanels(mode ? this.panels[mode] : null, () => {
        this.placeCommon(mode);
        this.showSummaryFor(mode);
        if (mode === 'pickup' && this.storesPending && !this.stores.length && heldHeight) {
          this.panels.pickup.style.minHeight = `${heldHeight}px`;
        }
      });
      this.form.classList.toggle('has-mode', !!mode);
      if (mode === 'pickup') this.renderPickupSummary();
      // En envío a domicilio el resumen siempre está: si aún no hay tarifa, en skeleton.
      if (mode === 'delivery' && (this.shippingEl.hidden || this.shippingPlaceholder)) {
        this.markShippingUpdating();
        this.refreshCartSummary();
      }

      if (mode === 'delivery' && !this.ubigeoLoaded) {
        this.ubigeoLoaded = true;
        const regions = (data) => limaFirst(data.map(([code, name]) => ({ id: code, name })));
        this.loadOptions('region', regions, this.savedUbigeo.region).then((restored) => {
          // Sin catálogo (red caída), se vuelve a intentar al elegir otra vez envío a domicilio.
          if (restored === null) this.ubigeoLoaded = false;
          if (restored) this.onRegionChange(this.savedUbigeo);
          else this.expand('delivery');
        });
      }
      if (mode === 'pickup' && !this.storesLoaded) {
        this.storesLoaded = true;
        this.loadStores();
      }
      this.renderSaveLinks();
    }

    releasePanelHeight(panel) {
      if (panel) panel.style.minHeight = '';
    }

    // Transición «fade through» entre paneles: primero se desvanece el que sale (rápido, sin
    // moverse de su lugar), luego se intercambia el contenido —la altura se ajusta una sola
    // vez— y entra el nuevo con un fundido suave. Nunca se ven los dos superpuestos.
    switchPanels(incoming, swap) {
      clearTimeout(this.panelTimer);
      const leaving = Object.values(this.panels).filter((panel) => panel !== incoming && !panel.hidden);
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const finish = () => {
        leaving.forEach((panel) => {
          panel.hidden = true;
          panel.classList.remove('is-leaving');
          this.releasePanelHeight(panel);
        });
        swap();
        if (!incoming) return;
        incoming.classList.remove('is-leaving');
        if (incoming.hidden) incoming.hidden = false;
      };

      if (!leaving.length || !this.isOpen || reduceMotion) {
        finish();
        return;
      }
      leaving.forEach((panel) => panel.classList.add('is-leaving'));
      this.sheet.querySelectorAll('[data-enova-summary-for]:not([hidden])').forEach((el) => el.classList.add('is-leaving'));
      this.panelTimer = setTimeout(finish, 150);
    }

    // Resumen de montos del modo activo (en el pie, junto a «Ir a pagar»).
    showSummaryFor(mode) {
      this.sheet.querySelectorAll('[data-enova-summary-for]').forEach((el) => {
        const active = el.dataset.enovaSummaryFor === mode;
        const wasHidden = el.hidden;
        el.classList.remove('is-leaving');
        el.hidden = !active;
        if (active && wasHidden) this.flash(el, 'is-entering', 280);
      });
    }

    // Animación puntual (valores recién calculados, tiendas que llegan, campo RUC): la clase se
    // pone solo si el elemento está a la vista y se retira al terminar. Si quedara puesta, el
    // navegador la volvería a correr cada vez que el panel reaparece (el parpadeo del final).
    flash(el, className, duration = 320) {
      clearTimeout(el.enovaFlashTimer);
      el.classList.remove(className);
      if (!this.isOpen || el.closest('[hidden]')) return;
      void el.offsetWidth;
      el.classList.add(className);
      el.enovaFlashTimer = setTimeout(() => el.classList.remove(className), duration);
    }

    // Tus datos y comprobante: un solo bloque para ambos modos, al final del panel activo.
    placeCommon(mode) {
      const panel = mode && this.panels[mode];
      this.commonEl.hidden = !panel;
      if (!panel) return;
      panel.appendChild(this.commonEl);
    }

    /* ---------------- Tus datos ---------------- */

    restoreContact() {
      const saved = this.saved || {};
      const customer = window.ShopifyCustomer && window.ShopifyCustomer.logged_in ? window.ShopifyCustomer : {};
      const phoneDigits = (value) => {
        const digits = String(value || '').replace(/\D/g, '');
        const local = digits.length === 11 && digits.startsWith('51') ? digits.slice(2) : digits;
        return PHONE_PATTERN.test(local) ? local : '';
      };
      const values = {
        firstName: saved.firstName || customer.firstName || '',
        lastName: saved.lastName || customer.lastName || '',
        email: saved.email || customer.email || '',
        phone: phoneDigits(saved.phone) || phoneDigits(customer.phone),
      };
      Object.keys(values).forEach((key) => {
        this.contactFields[key].value = values[key];
      });
    }

    // Valida tus datos. Devuelve los valores o null; en error marca los campos y deja el mensaje
    // en `this.contactMessage`.
    readContact({ mark = true } = {}) {
      const values = {};
      Object.keys(this.contactFields).forEach((key) => {
        values[key] = this.contactFields[key].value.trim();
      });
      const invalid = [];
      let message = '';
      ['firstName', 'lastName', 'email', 'phone'].forEach((key) => {
        if (!values[key]) {
          invalid.push(key);
          message = message || this.msg.msgContactRequired;
        }
      });
      if (values.email && !EMAIL_PATTERN.test(values.email)) {
        invalid.push('email');
        message = message || this.msg.msgEmailInvalid;
      }
      if (values.phone && !PHONE_PATTERN.test(values.phone)) {
        invalid.push('phone');
        message = message || this.msg.msgPhoneInvalid;
      }
      this.contactMessage = message;
      this.invalidContactField = invalid.length ? this.contactFields[invalid[0]] : null;
      if (mark) {
        Object.keys(this.contactFields).forEach((key) => {
          this.contactFields[key].classList.toggle('has-error', invalid.includes(key));
        });
      }
      return message ? null : values;
    }

    contactSaved(contact) {
      return {
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        phone: contact.phone,
      };
    }

    /* ---------------- Documento y comprobante ---------------- */

    // Arma los tipos y reaplica lo guardado mientras el cliente no toque el bloque.
    renderDocumentTypes() {
      const select = this.documentFields.type;
      const saved = this.saved || {};
      const savedType = this.documents.person.find(
        (doc) => doc.value === saved.documentType || (doc.legacy && doc.legacy === String(saved.documentType))
      );
      const preferred = this.documentTouched ? select.value : savedType ? savedType.value : select.value;
      select.innerHTML = this.documents.person
        .map((doc) => `<option value="${escapeHTML(doc.value)}">${escapeHTML(doc.label)}</option>`)
        .join('');
      if (this.documents.person.some((doc) => doc.value === preferred)) select.value = preferred;
      if (!this.documentTouched) {
        this.documentFields.number.value = saved.documentNumber || '';
        this.documentFields.company.value = saved.ruc || '';
      }
      this.applyDocumentType();

      const company = this.documents.company;
      if (company) {
        this.documentFields.companyLabel.textContent = company.label;
        this.documentFields.company.maxLength = company.maxLen;
        this.documentFields.company.inputMode = company.numeric === false ? 'text' : 'numeric';
      }
      this.sanitizeField(this.documentFields.company, company);
    }

    selectedDocument() {
      const value = this.documentFields.type.value;
      return this.documents.person.find((doc) => doc.value === value) || this.documents.person[0];
    }

    // Largo y teclado del número según el tipo. Al cambiar de tipo se conserva lo escrito
    // (solo se recorta lo que el nuevo tipo no admite).
    applyDocumentType() {
      const doc = this.selectedDocument();
      const input = this.documentFields.number;
      input.maxLength = doc.maxLen;
      input.inputMode = doc.numeric === false ? 'text' : 'numeric';
      this.sanitizeField(input, doc);
      this.renderDocumentSave();
    }

    sanitizeField(input, doc) {
      const clean = sanitizeDocument(input.value, doc);
      if (clean !== input.value) input.value = clean;
    }

    toggleInvoice(needsInvoice, { focus = false } = {}) {
      this.documentFields.invoice.checked = needsInvoice;
      // Solo se anima cuando lo hace el cliente con la hoja abierta; al restaurar datos, directo.
      // Mientras se despliega, la hoja sigue al contenido cuadro a cuadro: el resumen no salta.
      const animate = focus && this.canAnimate();
      if (animate) this.freezeHeightAnimation(360);
      this.setCollapse(this.documentFields.companyField, needsInvoice, animate);
      this.renderDocumentSave();
      // Foco en el RUC al terminar de desplegarse. En pantallas táctiles no se fuerza: abrir el
      // teclado a mitad de la animación mueve la vista; el cliente toca el campo cuando quiera.
      clearTimeout(this.invoiceFocusTimer);
      if (needsInvoice && focus) this.reveal(this.documentFields.companyField);
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (needsInvoice && focus && !coarse) {
        this.invoiceFocusTimer = setTimeout(
          () => this.documentFields.company.focus({ preventScroll: true }),
          animate ? 300 : 0
        );
      }
    }

    // Mismas claves de customerData que /cart: lo escrito en un lado aparece en el otro.
    restoreDocument() {
      this.renderDocumentTypes();
      this.toggleInvoice((this.saved || {}).billingType === 'factura');
    }

    documentError(doc, value) {
      if (!value) return String(this.msg.msgDocumentRequired || '').replace('__LABEL__', doc.label);
      const length =
        doc.minLen === doc.maxLen
          ? String(doc.minLen)
          : String(this.msg.msgDocumentRange || '').replace('__MIN__', doc.minLen).replace('__MAX__', doc.maxLen);
      const template = doc.numeric === false ? this.msg.msgDocumentAlphanumeric : this.msg.msgDocumentNumeric;
      return String(template || '').replace('__LABEL__', doc.label).replace('__LENGTH__', length);
    }

    // Misma validación que /cart (largo y caracteres por tipo; con factura, RUC de 11 dígitos).
    // Devuelve los datos o null; en error marca el campo y deja el mensaje en
    // `this.documentMessage`.
    readDocument({ mark = true } = {}) {
      const doc = this.selectedDocument();
      const number = this.documentFields.number.value.trim();
      const needsInvoice = this.documentFields.invoice.checked;
      const company = this.documents.company;
      const ruc = this.documentFields.company.value.trim();
      this.documentMessage = '';
      this.invalidDocumentField = null;
      const invalid = [];
      const fail = (input, message) => {
        invalid.push(input);
        if (!this.documentMessage) {
          this.documentMessage = message;
          this.invalidDocumentField = input;
        }
      };

      if (!isDocumentValid(number, doc)) fail(this.documentFields.number, this.documentError(doc, number));
      if (needsInvoice && !isDocumentValid(ruc, company)) fail(this.documentFields.company, this.documentError(company, ruc));
      if (mark) {
        [this.documentFields.number, this.documentFields.company].forEach((input) => {
          input.classList.toggle('has-error', invalid.includes(input));
        });
      }
      if (this.documentMessage) return null;

      return {
        doc,
        number,
        invoice: needsInvoice,
        ruc: needsInvoice ? ruc : '',
        saved: {
          documentType: doc.legacy || doc.value,
          documentNumber: number,
          billingType: needsInvoice ? 'factura' : 'boleta',
          ruc: needsInvoice ? ruc : '',
        },
      };
    }

    /* ---------------- Envío a domicilio ---------------- */

    restoreAddress() {
      const saved = this.saved || {};
      this.addressFields.address.value = saved.address || '';
      this.addressFields.reference.value = saved.reference && saved.reference !== '-' ? saved.reference : '';
    }

    // Llena un select con los ubigeos del INEI; `itemsOf(catálogo)` da sus opciones
    // ({ id, name, code }). Devuelve true si pudo restaurar `savedValue`, null si no hay catálogo.
    loadOptions(level, itemsOf, savedValue) {
      const select = this.selects[level];
      select.disabled = true;
      select.innerHTML = `<option value="">${escapeHTML(this.msg.msgLoading)}</option>`;

      return loadUbigeo()
        .then((data) => {
          const items = itemsOf(data);
          let options = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>`;
          items.forEach((item) => {
            options += `<option value="${escapeHTML(item.name)}" data-id="${escapeHTML(item.id)}" data-code="${escapeHTML(item.code || '')}">${escapeHTML(item.name)}</option>`;
          });
          select.innerHTML = options;
          select.disabled = false;

          const saved = savedValue && items.find((item) => sameName(item.name, savedValue));
          if (saved) {
            select.value = saved.name;
            return true;
          }
          return false;
        })
        .catch(() => {
          select.innerHTML = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>`;
          select.disabled = false;
          return null;
        });
    }

    resetSelect(level) {
      const select = this.selects[level];
      select.innerHTML = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>`;
      select.disabled = true;
      if (level === 'district') this.renderSaveLinks();
    }

    selectedOption(level) {
      const select = this.selects[level];
      return select.value ? select.options[select.selectedIndex] : null;
    }

    onRegionChange(saved = {}) {
      this.clearError();
      this.selects.region.classList.remove('has-error');
      this.resetSelect('province');
      this.resetSelect('district');
      this.resetShipping();
      const region = this.selectedOption('region');
      if (!region) return;

      const restoreProvince = sameName(saved.region, region.value) ? saved.province : null;
      const provinces = (data) => {
        const found = data.find(([code]) => code === region.dataset.id);
        return limaFirst((found ? found[2] : []).map(([code, name]) => ({ id: code, name })));
      };
      this.loadOptions('province', provinces, restoreProvince).then((restored) => {
        if (restored) this.onProvinceChange(saved);
        else if (saved.province) this.expand('delivery');
      });
    }

    onProvinceChange(saved = {}) {
      this.clearError();
      this.selects.province.classList.remove('has-error');
      this.resetSelect('district');
      this.resetShipping();
      const region = this.selectedOption('region');
      const province = this.selectedOption('province');
      if (!region || !province) return;

      const restoreDistrict = sameName(saved.province, province.value) ? saved.district : null;
      const districts = (data) => {
        const found = data.find(([code]) => code === region.dataset.id);
        const list = found ? found[2].find(([code]) => code === province.dataset.id) : null;
        return (list ? list[2] : []).map(([name, ubigeo]) => ({ id: ubigeo, name, code: ubigeo }));
      };
      this.loadOptions('district', districts, restoreDistrict).then((restored) => {
        if (restored) this.onDistrictChange();
        else if (saved.district) this.expand('delivery');
      });
    }

    onDistrictChange() {
      this.clearError();
      this.selects.district.classList.remove('has-error');
      this.renderSaveLinks();
      const district = this.selectedOption('district');
      if (!district || !district.dataset.code) {
        this.resetShipping();
        return;
      }
      this.fetchRates(district.dataset.code);
    }

    // ¿Ya hay un resumen con total a la vista? Entonces se conserva al recalcular.
    hasShippingSummary() {
      return !this.shippingEl.hidden && !!this.shippingEl.querySelector('[data-enova-checkout-total]');
    }

    // Departamento o provincia cambió (o se vació el distrito): el resumen se queda con los
    // valores que van a cambiar en skeleton en vez de desaparecer.
    resetShipping() {
      this.ratesRequest += 1;
      this.ratesInFlight = false;
      this.ratesUbigeo = null;
      this.methods = [];
      this.setScheduleEligible(false);
      this.shippingEl.classList.remove('is-loading');
      this.markShippingUpdating();
    }

    // UI optimista: el resumen de envío a domicilio siempre está a la vista. Mientras no hay
    // tarifa, el envío y el total llevan un skeleton sutil; si todavía no hubo ninguna, se pinta
    // la misma estructura con los productos y descuentos reales del carrito.
    markShippingUpdating() {
      this.shippingEl.hidden = false;
      this.shippingEl.setAttribute('aria-busy', 'true');
      this.shippingEl.classList.remove('is-refreshed');
      this.shippingEl.classList.add('is-updating');
      if (!this.hasShippingSummary() || this.shippingPlaceholder) this.renderShippingPlaceholder();
    }

    // { count, original, discount, total } del pie del mini-cart (en céntimos), o null.
    readDrawerSummary() {
      const el = this.drawer.querySelector('[data-enova-cart-total]');
      if (!el) return null;
      const read = (key) => Number(el.dataset[key]) || 0;
      return {
        count: read('enovaCartCount'),
        original: read('enovaCartOriginal'),
        discount: read('enovaCartDiscount'),
        total: read('enovaCartTotal'),
      };
    }

    renderShippingPlaceholder() {
      this.shippingPlaceholder = true;
      if (!this.cartSummary) this.cartSummary = this.readDrawerSummary();
      const bar = (width) => `<span class="enova-minicart-sk" style="width:${width}px"></span>`;
      const products = this.cartSummary
        ? this.renderSummaryLines()
        : `<div class="enova-minicart-sheet__line">${bar(72)}${bar(44)}</div>`;
      this.shippingEl.innerHTML = `
        <span class="enova-minicart-sr">${escapeHTML(this.msg.msgCalculating)}</span>
        ${products}
        <div class="enova-minicart-sheet__method is-single">
          <span class="enova-minicart-sheet__method-text">
            <span class="enova-minicart-sheet__method-name">${escapeHTML(this.msg.msgShipping)}</span>
            <span class="enova-minicart-sheet__method-desc" style="width:112px">&nbsp;</span>
          </span>
          <span class="enova-minicart-sheet__method-price" style="width:56px">&nbsp;</span>
        </div>
        <div class="enova-minicart-sheet__total">
          <span class="enova-minicart-sheet__total-label">${escapeHTML(this.msg.msgTotal)}</span>
          <span class="enova-minicart-sheet__total-value" style="width:72px" data-enova-checkout-total>&nbsp;</span>
        </div>`;
    }

    // Productos y descuentos del carrito para el resumen, aunque aún no haya distrito.
    refreshCartSummary() {
      return getJSON(`${root()}cart.js`)
        .then((cart) => {
          const previous = JSON.stringify(this.cartSummary);
          this.cartSummary = {
            count: Number(cart.item_count) || 0,
            original: Number(cart.original_total_price) || 0,
            discount: Number(cart.total_discount) || 0,
            total: Number(cart.total_price) || 0,
          };
          // Solo se repinta si el carrito cambió: nada se mueve si los montos son los mismos.
          const changed = previous !== JSON.stringify(this.cartSummary);
          if (changed && this.shippingPlaceholder && this.mode === 'delivery') this.renderShippingPlaceholder();
          if (changed && this.mode === 'delivery' && !this.shippingPlaceholder) this.renderMethods();
          if (changed && this.mode === 'pickup') this.renderPickupSummary();
        })
        .catch(() => {});
    }

    // Tarifas del distrito para este carrito, pedidas a Shopify (las mismas del checkout).
    fetchRates(ubigeo) {
      const request = ++this.ratesRequest;
      this.methods = [];
      this.ratesUbigeo = ubigeo;
      this.ratesInFlight = true;
      this.shippingEl.classList.add('is-loading');
      this.markShippingUpdating();
      const region = this.selectedOption('region');
      const province = this.selectedOption('province');
      const district = this.selectedOption('district');
      const address = {
        provinceCode: provinceCodeFor({ ubigeo, region: region && region.value, province: province && province.value }),
        city: district ? district.value : '',
        zip: ubigeo,
      };

      return getJSON(`${root()}cart.js`)
        .then((cart) => {
          this.cartSummary = {
            count: Number(cart.item_count) || 0,
            original: Number(cart.original_total_price) || 0,
            discount: Number(cart.total_discount) || 0,
            total: Number(cart.total_price) || 0,
          };
          this.ratesCartSig = this.cartSignature(this.cartSummary);
          return shopifyRates(address);
        })
        .then((methods) => {
          if (request !== this.ratesRequest) return;
          this.ratesInFlight = false;
          // El envío programado se arma en la hoja (fecha y horario) sobre la tarifa regular. Si
          // un mismo código llegara repetido, el checkout cotiza el primero: aquí también.
          const seen = new Set();
          this.methods = methods.filter((method) => {
            if (seen.has(method.code)) return false;
            seen.add(method.code);
            return true;
          });
          this.setScheduleEligible(!!this.methods.length && matchesUbigeo(SCHEDULED.ubigeo_prefix, ubigeo));
          this.renderMethods();
        })
        .catch(() => {
          if (request !== this.ratesRequest) return;
          this.ratesInFlight = false;
          this.methods = [];
          this.setScheduleEligible(false);
          this.renderMethods();
        });
    }

    // Nombre, plazo y precio del método para el ubigeo, como los mostrará el checkout:
    //   - Nombre y plazo, los de la tarifa; en envío programado, la fecha y el horario elegidos.
    //   - En S/ 0: «Gratis» en Lima y Callao, «Por coordinar» en provincias.
    //   - Desde el monto de envío gratis, «Gratis» (el descuento automático lo aplica en el checkout).
    methodView(method, ubigeo) {
      const home = matchesUbigeo(HOME_DELIVERY.ubigeo_prefix, ubigeo);
      const cents = Number(method.cents) || 0;
      const free = cents > 0 && FREE_SHIPPING_THRESHOLD > 0 && this.cartSummary && this.cartSummary.total >= FREE_SHIPPING_THRESHOLD;
      let description = method.description || (home ? HOME_DELIVERY.description : '');
      if (this.shippingType === 'scheduled' && this.scheduleEligible) {
        const schedule = this.readSchedule();
        description = schedule ? this.scheduleText(schedule) : this.msg.msgShippingScheduledMeta;
      }
      return {
        name: method.name || (home ? HOME_DELIVERY.name : this.msg.msgShipping),
        description,
        cents: cents > 0 && !free ? cents : 0,
        priceLabel: cents > 0 ? (free ? this.msg.msgFree : formatMoney(cents)) : home ? this.msg.msgFree : this.msg.msgToCoordinate,
      };
    }

    renderMethods() {
      const wasUpdating = this.shippingEl.classList.contains('is-updating');
      const current = this.selectedMethod();
      this.shippingPlaceholder = false;
      this.shippingEl.classList.remove('is-loading', 'is-updating');
      this.shippingEl.removeAttribute('aria-busy');
      // Los valores nuevos entran con un fundido corto, sin mover el resto del resumen.
      if (wasUpdating) this.flash(this.shippingEl, 'is-refreshed');
      if (!this.methods.length) {
        this.shippingEl.innerHTML = `<span class="enova-minicart-sheet__shipping-empty">${escapeHTML(this.msg.msgUnavailable)}</span>`;
        this.expand('delivery');
        this.renderSaveLinks();
        return;
      }

      const ubigeo = this.ratesUbigeo;
      const selected = current && this.methods.some((method) => method.code === current.code) ? current.code : this.methods[0].code;
      const single = this.methods.length === 1;
      this.shippingEl.innerHTML =
        this.renderSummaryLines() +
        this.methods
          .map((method) => {
            const view = this.methodView(method, ubigeo);
            const description = view.description
              ? `<span class="enova-minicart-sheet__method-desc">${escapeHTML(view.description)}</span>`
              : '';
            return `
            <label class="enova-minicart-sheet__method${single ? ' is-single' : ''}">
              <input type="radio" name="enova-minicart-method" value="${escapeHTML(method.code)}"${method.code === selected ? ' checked' : ''}${single ? ' hidden' : ''}>
              <span class="enova-minicart-sheet__method-text">
                <span class="enova-minicart-sheet__method-name">${escapeHTML(view.name)}</span>
                ${description}
              </span>
              <span class="enova-minicart-sheet__method-price">${escapeHTML(view.priceLabel)}</span>
            </label>`;
          })
          .join('');
      this.shippingEl.insertAdjacentHTML(
        'beforeend',
        `<div class="enova-minicart-sheet__total">
          <span class="enova-minicart-sheet__total-label">${escapeHTML(this.msg.msgTotal)}</span>
          <span class="enova-minicart-sheet__total-value" data-enova-checkout-total></span>
        </div>`
      );
      if (this.regularMetaEl) {
        // Bajo «Envío regular», el plazo de la tarifa aunque esté elegido el programado.
        this.regularMetaEl.textContent =
          this.methods[0].description || (matchesUbigeo(HOME_DELIVERY.ubigeo_prefix, ubigeo) ? HOME_DELIVERY.description : '');
      }
      this.renderTotal();
      this.renderSaveLinks();
      this.renderSummary();
    }

    // «Productos (n)» a precio original y, si hay cupón o descuento, la línea de descuento:
    // así Productos − Descuento + Envío = Total, igual que en el checkout.
    renderSummaryLines() {
      const summary = this.cartSummary;
      if (!summary) return '';
      const reference = Math.max(summary.original, summary.total);
      const savings = reference - summary.total;
      const label = String(this.msg.msgProducts || '').replace('__COUNT__', summary.count);
      let html = `
        <div class="enova-minicart-sheet__line">
          <span>${escapeHTML(label)}</span>
          <span>${escapeHTML(formatMoney(reference))}</span>
        </div>`;
      if (savings > 0) {
        html += `
          <div class="enova-minicart-sheet__line enova-minicart-sheet__line--discount">
            <span>${escapeHTML(this.msg.msgDiscount)}</span>
            <span>−${escapeHTML(formatMoney(savings))}</span>
          </div>`;
      }
      return html;
    }

    // Total = total del carrito (con descuentos) + envío elegido. Solo en envío a domicilio.
    renderTotal() {
      const method = this.selectedMethod();
      if (!method) return;
      const view = this.methodView(method, this.ratesUbigeo);
      const totalEl = this.shippingEl.querySelector('[data-enova-checkout-total]');
      if (totalEl) totalEl.textContent = formatMoney((this.cartSummary ? this.cartSummary.total : 0) + view.cents);
      // El plazo cambia con el tipo de envío y la fecha elegida.
      const descEl = this.shippingEl.querySelector(`input[value="${CSS.escape(method.code)}"]`);
      const label = descEl && descEl.closest('.enova-minicart-sheet__method');
      const desc = label && label.querySelector('.enova-minicart-sheet__method-desc');
      if (desc && view.description) desc.textContent = view.description;
    }

    selectedMethod() {
      const input = this.shippingEl.querySelector('input[name="enova-minicart-method"]:checked');
      if (!input) return null;
      return this.methods.find((method) => method.code === input.value) || null;
    }

    /* ---------------- Envío regular o programado ---------------- */

    // Solo en los distritos de entrega programada (config) y con tarifa: si no aplica, regular.
    setScheduleEligible(eligible) {
      this.scheduleEligible = eligible;
      const animate = this.canAnimate();
      if (animate) this.freezeHeightAnimation(380);
      this.setCollapse(this.collapses['shipping-type'], eligible, animate);
      this.setShippingType(eligible ? this.shippingType : 'regular', { keep: true });
    }

    setShippingType(type, { focus = false, keep = false } = {}) {
      const scheduled = type === 'scheduled' && this.scheduleEligible;
      if (!keep || this.scheduleEligible) this.shippingType = scheduled ? 'scheduled' : 'regular';
      this.typesEl.querySelectorAll('input[name="enova-minicart-shipping-type"]').forEach((input) => {
        const active = input.value === (scheduled ? 'scheduled' : 'regular');
        input.checked = active;
        input.closest('.enova-minicart-mode').classList.toggle('is-active', active);
      });
      if (scheduled) this.renderScheduleOptions();
      const animate = focus && this.canAnimate();
      if (animate) this.freezeHeightAnimation(360);
      this.setCollapse(this.collapses.schedule, scheduled, animate);
      // Solo un cambio del cliente limpia el error: recalcular la tarifa no debe borrarlo.
      if (!keep) this.clearError();
      this.renderTotal();
      this.renderSaveLinks();
      this.renderSummary();
      if (scheduled && focus) {
        this.reveal(this.collapses.schedule);
        const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        if (!coarse) setTimeout(() => this.scheduleSelects.date.focus({ preventScroll: true }), animate ? 300 : 0);
      }
    }

    // Fechas (según el día y la hora de Lima) y horarios; conserva lo elegido si sigue vigente.
    renderScheduleOptions() {
      const { date, slot } = this.scheduleSelects;
      const currentDate = date.value;
      const currentSlot = slot.value || (this.saved && this.saved.scheduledSlot) || '';
      const dates = scheduleDates();
      date.innerHTML =
        `<option value="">${escapeHTML(this.msg.msgSelect)}</option>` +
        dates.map((item) => `<option value="${escapeHTML(item.value)}">${escapeHTML(item.label)}</option>`).join('');
      if (dates.some((item) => item.value === currentDate)) date.value = currentDate;
      slot.innerHTML =
        `<option value="">${escapeHTML(this.msg.msgSelect)}</option>` +
        (SCHEDULED.slots || [])
          .map((item) => `<option value="${escapeHTML(item.value)}">${escapeHTML(item.label)}</option>`)
          .join('');
      if ((SCHEDULED.slots || []).some((item) => item.value === currentSlot)) slot.value = currentSlot;
    }

    // { date, dateLabel, slot, slotLabel } o null si falta algo.
    readSchedule() {
      const { date, slot } = this.scheduleSelects;
      if (!date.value || !slot.value) return null;
      return {
        date: date.value,
        dateLabel: date.options[date.selectedIndex].textContent,
        slot: slot.value,
        slotLabel: slot.options[slot.selectedIndex].textContent,
      };
    }

    scheduleText(schedule) {
      return String(this.msg.msgScheduledDetail || '')
        .replace('__DATE__', schedule.dateLabel)
        .replace('__SLOT__', schedule.slotLabel);
    }

    // Entrega a domicilio completa: ubigeo, dirección, referencia, tarifa y, si es programado,
    // fecha y horario. Devuelve los datos o null; en error deja el mensaje en `this.deliveryMessage`.
    readDelivery({ mark = true } = {}) {
      this.deliveryMessage = '';
      this.invalidDeliveryField = null;
      const fail = (fields, message) => {
        if (mark) fields.forEach((field) => field.classList.add('has-error'));
        if (!this.deliveryMessage) {
          this.deliveryMessage = message;
          this.invalidDeliveryField = fields[0] || null;
        }
      };
      const region = this.selectedOption('region');
      const province = this.selectedOption('province');
      const district = this.selectedOption('district');
      const missingUbigeo = [
        ['region', region],
        ['province', province],
        ['district', district],
      ]
        .filter(([, option]) => !option)
        .map(([level]) => this.selects[level]);
      if (missingUbigeo.length) fail(missingUbigeo, this.msg.msgRequired);

      const address = this.addressFields.address.value.trim();
      const reference = this.addressFields.reference.value.trim();
      const missingAddress = [];
      if (!address) missingAddress.push(this.addressFields.address);
      if (!reference) missingAddress.push(this.addressFields.reference);
      if (missingAddress.length) fail(missingAddress, this.msg.msgAddressRequired);

      if (!missingUbigeo.length) {
        if (this.shippingEl.classList.contains('is-loading') || this.ratesInFlight) fail([], this.msg.msgCalculating);
        else if (!this.selectedMethod()) fail([], this.msg.msgUnavailable);
      }

      let schedule = null;
      if (this.shippingType === 'scheduled' && this.scheduleEligible) {
        schedule = this.readSchedule();
        if (!schedule) {
          const { date, slot } = this.scheduleSelects;
          fail([date, slot].filter((select) => !select.value), this.msg.msgScheduleRequired);
        }
      }
      if (this.deliveryMessage) return null;

      return {
        region: region.value,
        province: province.value,
        district: district.value,
        ubigeo: district.dataset.code,
        address,
        reference,
        method: this.selectedMethod(),
        schedule,
      };
    }

    deliverySaved(delivery) {
      return {
        shippingMethod: 'delivery',
        region: delivery.region,
        city: delivery.province,
        locality: delivery.district,
        address: delivery.address,
        reference: delivery.reference,
        shippingMethodType: delivery.schedule ? 'programmed' : 'regular',
        scheduledSlot: delivery.schedule ? delivery.schedule.slot : (this.saved && this.saved.scheduledSlot) || '',
      };
    }

    /* ---------------- Retiro en tienda ---------------- */

    // Tiendas de retiro (config) + stock del carrito en cada una (sección enova-pickup-stock por
    // variante, con el store_availabilities de Shopify). Se revalida al reabrir la hoja.
    loadStores() {
      const request = (this.storesRequest = (this.storesRequest || 0) + 1);
      this.storesPending = true;
      if (!this.stores.length) this.storesEl.innerHTML = this.renderStoresSkeleton();

      return this.fetchPickupStock().then((stock) => {
        if (request !== this.storesRequest) return;
        this.storesPending = false;
        this.stores = PICKUP_STORES.map((store) =>
          Object.assign({}, store, { locationId: String(store.locationId), availability: this.storeAvailability(store, stock) })
        );
        this.renderStores();
      });
    }

    // { ok, lines: [{variantId, title, quantity}], byVariant: {variantId: {locationId: {available, pickUpTime}}} }.
    // Si falla, ok=false y las tiendas quedan elegibles (el checkout valida igual el stock).
    fetchPickupStock() {
      return getJSON(`${root()}cart.js`)
        .then((cart) => {
          const lines = [];
          (cart.items || []).forEach((item) => {
            if (item.requires_shipping === false) return;
            const line = lines.find((entry) => entry.variantId === String(item.variant_id));
            if (line) line.quantity += item.quantity;
            else lines.push({ variantId: String(item.variant_id), title: item.product_title || item.title, quantity: item.quantity });
          });
          if (!lines.length) return { ok: false, lines, byVariant: {} };

          return Promise.all(
            lines.map((line) =>
              fetch(`${root()}variants/${line.variantId}/?section_id=enova-pickup-stock`)
                .then((response) => {
                  if (!response.ok) throw new Error(`HTTP ${response.status}`);
                  return response.text();
                })
                .then((html) => {
                  const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-enova-pickup-stock]');
                  const data = JSON.parse(node.textContent);
                  const locations = {};
                  (data.locations || []).forEach((location) => {
                    locations[String(location.id)] = {
                      available: location.available === true,
                      pickUpTime: String(location.pickUpTime || '').trim(),
                    };
                  });
                  return [line.variantId, locations];
                })
            )
          ).then((entries) => ({ ok: true, lines, byVariant: Object.fromEntries(entries) }));
        })
        .catch(() => ({ ok: false, lines: [], byVariant: {} }));
    }

    // { selectable, known, missing: [títulos], total, pickUpTime } de una tienda para el carrito.
    storeAvailability(store, stock) {
      if (!stock.ok) return { selectable: true, known: false, missing: [], total: 0, pickUpTime: '' };
      const locationId = String(store.locationId);
      const missing = stock.lines.filter((line) => {
        const location = (stock.byVariant[line.variantId] || {})[locationId];
        return !(location && location.available);
      });
      const firstAvailable = stock.lines
        .map((line) => (stock.byVariant[line.variantId] || {})[locationId])
        .find((location) => location && location.available);
      return {
        selectable: missing.length === 0,
        known: true,
        missing: missing.map((line) => line.title),
        total: stock.lines.length,
        pickUpTime: firstAvailable ? firstAvailable.pickUpTime : '',
      };
    }

    savedStore() {
      const id = this.saved && this.saved.pickupStore;
      return id ? PICKUP_STORES.find((store) => String(store.locationId) === String(id)) || null : null;
    }

    pickupSaved(store) {
      return { shippingMethod: 'recojo-tienda', pickupStore: String(store.locationId) };
    }

    // Resumen de retiro, igual que el de envío: productos, descuentos, retiro (gratis) y total.
    renderPickupSummary() {
      const el = this.pickupSummaryEl;
      if (!el) return;
      if (!this.cartSummary) this.cartSummary = this.readDrawerSummary();
      const store = this.selectedStore();
      const cartTotal = this.cartSummary ? this.cartSummary.total : 0;
      const description = store ? `<span class="enova-minicart-sheet__method-desc">${escapeHTML(store.name)}</span>` : '';
      el.innerHTML = `
        ${this.renderSummaryLines()}
        <div class="enova-minicart-sheet__method is-single">
          <span class="enova-minicart-sheet__method-text">
            <span class="enova-minicart-sheet__method-name">${escapeHTML(this.msg.msgPickupLabel)}</span>
            ${description}
          </span>
          <span class="enova-minicart-sheet__method-price">${escapeHTML(this.msg.msgFree)}</span>
        </div>
        <div class="enova-minicart-sheet__total">
          <span class="enova-minicart-sheet__total-label">${escapeHTML(this.msg.msgTotal)}</span>
          <span class="enova-minicart-sheet__total-value">${escapeHTML(formatMoney(cartTotal))}</span>
        </div>`;
    }

    // Tarjetas de tienda en skeleton (misma forma que las reales) mientras llega el stock.
    renderStoresSkeleton() {
      const bar = (width) => `<span class="enova-minicart-sk" style="width:${width}px"></span>`;
      const item = `
        <div class="enova-minicart-store is-skeleton" aria-hidden="true">
          <span class="enova-minicart-store__radio"></span>
          <span class="enova-minicart-store__text">${bar(128)}${bar(184)}${bar(96)}</span>
          <span class="enova-minicart-store__price">${bar(36)}</span>
        </div>`;
      return `<span class="enova-minicart-sr">${escapeHTML(this.msg.msgLoading)}</span>${item.repeat(Math.max(PICKUP_STORES.length, 1))}`;
    }

    // Texto de stock de la tienda: con stock (y cuándo está listo), o qué productos le faltan.
    // Shopify deja vacío el plazo de retiro en español: se usa el de la config de la tienda.
    stockNote(store) {
      const availability = store.availability;
      if (!availability.known) return '';
      if (availability.selectable) {
        const readyText = availability.pickUpTime || store.readyText || '';
        const ready = readyText ? ` · ${escapeHTML(readyText)}` : '';
        return `<span class="enova-minicart-store__stock is-available">${escapeHTML(this.msg.msgStoreAvailable)}${ready}</span>`;
      }
      let text;
      if (availability.total > 1 && availability.missing.length >= availability.total) {
        text = this.msg.msgStoreUnavailable;
      } else {
        const names = availability.missing.slice(0, 2).join(', ');
        const rest = availability.missing.length - 2;
        const more = rest > 0 ? ` ${String(this.msg.msgStoreMore || '').replace('__COUNT__', rest)}` : '';
        text = String(this.msg.msgStoreMissing || '').replace('__PRODUCTS__', names + more);
      }
      return `<span class="enova-minicart-store__stock">${escapeHTML(text)}</span>`;
    }

    renderStores() {
      const wasSkeleton = !!this.storesEl.querySelector('.is-skeleton');
      // Con el contenido real ya puesto se suelta el alto conservado: la hoja pasa de una vez
      // al alto de las tiendas.
      this.releasePanelHeight(this.panels.pickup);
      if (wasSkeleton) this.flash(this.storesEl, 'is-revealed');
      if (!this.stores.length) {
        this.storesEl.innerHTML = `<p class="enova-minicart-stores__status">${escapeHTML(this.msg.msgStoresError)}</p>`;
        return;
      }

      const current = this.selectedStore();
      const savedStore = this.savedStore();
      const preferred = current ? current.locationId : savedStore ? String(savedStore.locationId) : '';
      const anySelectable = this.stores.some((store) => store.availability.selectable);

      let html = this.stores
        .map((store, index) => {
          const { selectable } = store.availability;
          const checked = selectable && store.locationId === preferred ? ' checked' : '';
          return `
            <label class="enova-minicart-store${selectable ? '' : ' is-disabled'}">
              <input type="radio" name="enova-minicart-store" value="${index}"${checked}${selectable ? '' : ' disabled'}>
              <span class="enova-minicart-store__radio" aria-hidden="true"></span>
              <span class="enova-minicart-store__text">
                <span class="enova-minicart-store__name">${escapeHTML(store.name)}</span>
                <span class="enova-minicart-store__address">${escapeHTML(store.address || '')}</span>
                ${this.stockNote(store)}
              </span>
              <span class="enova-minicart-store__price">${selectable ? escapeHTML(this.msg.msgFree) : ''}</span>
            </label>`;
        })
        .join('');

      if (!anySelectable) {
        html =
          `<div class="enova-minicart-stores__notice">
            <p>${escapeHTML(this.msg.msgPickupUnavailable)}</p>
            <button type="button" class="enova-minicart-stores__switch" data-enova-checkout-switch-delivery>
              ${escapeHTML(this.msg.msgSwitchDelivery)}
            </button>
          </div>` + html;
      }
      this.storesEl.innerHTML = html;
      this.renderPickupSummary();
      this.renderSaveLinks();
      if (this.compact.delivery && this.mode === 'pickup') {
        // La tienda guardada ya no tiene stock del carrito: se pide elegir de nuevo.
        if (this.selectedStore()) this.renderSummary();
        else this.expand('delivery');
      }
    }

    selectedStore() {
      const input = this.storesEl.querySelector('input[name="enova-minicart-store"]:checked');
      return input ? this.stores[Number(input.value)] || null : null;
    }

    /* ---------------- Envío del formulario ---------------- */

    showError(message) {
      if (!message) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.storesEl.classList.remove('has-error');
      this.modesEl.classList.remove('has-error');
    }

    setSubmitting(submitting) {
      this.submitting = submitting;
      this.submitBtn.disabled = submitting;
      this.submitBtn.classList.toggle('is-loading', submitting);
      this.submitBtn.setAttribute('aria-busy', submitting ? 'true' : 'false');
      if (!this.defaultLabel) this.defaultLabel = this.submitLabel.textContent;
      this.submitLabel.textContent = submitting ? this.msg.msgProcessing : this.defaultLabel;
    }

    // Lleva la vista al campo con error, si no está a la vista.
    focusField(field) {
      if (!field || typeof field.focus !== 'function') return;
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (!coarse) field.focus({ preventScroll: true });
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;
      this.clearError();

      if (!this.mode) {
        this.modesEl.classList.add('has-error');
        this.showError(this.msg.msgModeRequired);
        return;
      }

      // Se valida todo de una vez; el mensaje es el del primer bloque con error (de arriba abajo).
      let entrega;
      let entregaMessage = '';
      let entregaField = null;
      if (this.mode === 'pickup') {
        const store = this.selectedStore();
        entrega = store && store.availability.selectable ? store : null;
        if (!entrega) {
          this.storesEl.classList.add('has-error');
          entregaMessage = this.msg.msgStoreRequired;
        }
      } else {
        entrega = this.readDelivery();
        entregaMessage = this.deliveryMessage;
        entregaField = this.invalidDeliveryField;
      }
      const contact = this.readContact();
      const documentData = this.readDocument();

      if (!entrega || !contact || !documentData) {
        // Un error dentro del resumen abre esa sección para que se vea qué corregir.
        if (!entrega) this.expand('delivery');
        if (!contact) this.expand('contact');
        if (!documentData) this.expand('document');
        const [message, field] = !entrega
          ? [entregaMessage, entregaField]
          : !contact
            ? [this.contactMessage, this.invalidContactField]
            : [this.documentMessage, this.invalidDocumentField];
        this.showError(message);
        setTimeout(() => this.focusField(field || this.errorEl), this.canAnimate() ? 320 : 0);
        return;
      }

      const order = this.mode === 'pickup'
        ? this.buildPickupOrder(entrega, contact, documentData)
        : this.buildDeliveryOrder(entrega, contact, documentData);
      this.setSubmitting(true);
      this.goToCheckout(order).catch((error) => {
        console.warn('[enova-minicart] precheckout falló, se usa el checkout nativo', error);
        window.location.href = appendUTMs(nativeCheckoutUrl(order));
      });
    }

    // Atributos del pedido comunes a ambos modos (mismas claves que /cart).
    baseAttributes(contact, documentData) {
      const attributes = {
        pais: 'Peru',
        'comprobante de pago': documentData.invoice ? 'factura' : 'boleta',
        email: contact.email,
        nombre: contact.firstName,
        apellido: contact.lastName,
        telefono: contact.phone,
        'Documento Identidad': documentData.number,
        'Tipo Documento': documentData.doc.label,
      };
      if (documentData.invoice) attributes.ruc = documentData.ruc;
      return attributes;
    }

    // Empresa en la dirección (el checkout la rotula «DNI/CE o si desea factura RUC»): con
    // factura, el RUC; si no, el documento.
    companyLine(documentData) {
      return documentData.invoice ? documentData.ruc : documentData.number;
    }

    buildDeliveryOrder(delivery, contact, documentData) {
      const attributes = Object.assign(this.baseAttributes(contact, documentData), {
        'Modalidad despacho': 'delivery',
        departamento: delivery.region,
        provincia: delivery.province,
        distrito: delivery.district,
        ubigeo: delivery.ubigeo,
        direccion: delivery.address,
        referencia: delivery.reference,
        'tipo de envio': delivery.schedule ? 'programmed' : 'regular',
      });
      if (delivery.schedule) {
        attributes['fecha de envio'] = delivery.schedule.date;
        attributes['horario de envio'] = delivery.schedule.slot;
      }

      const shippingAddress = buildShippingAddress({
        firstName: contact.firstName,
        lastName: contact.lastName,
        phone: contact.phone,
        company: this.companyLine(documentData),
        address: delivery.address,
        reference: delivery.reference,
        region: delivery.region,
        province: delivery.province,
        district: delivery.district,
        ubigeo: delivery.ubigeo,
      });

      return {
        mode: 'delivery',
        email: contact.email,
        attributes,
        // El programado viaja como la tarifa regular («regular»), con su fecha y horario en los
        // atributos del pedido. La descripción (plazo o fecha y horario) es la que se muestra en
        // la hoja, y «Reveni Envíos» la pone bajo el método en el checkout.
        line: {
          dispatchMode: 'delivery',
          shippingMethod: delivery.method.code || 'regular',
          ubigeo: delivery.ubigeo,
          description: this.methodView(delivery.method, delivery.ubigeo).description,
        },
        buyerIdentity: {
          countryCode: COUNTRY,
          email: contact.email,
          phone: toE164(contact.phone),
          preferences: { delivery: { deliveryMethod: ['SHIPPING'] } },
        },
        delivery: { addresses: [{ address: { deliveryAddress: shippingAddress }, selected: true }] },
        shippingAddress,
        saved: Object.assign(
          { loaded: false },
          this.contactSaved(contact),
          documentData.saved,
          this.deliverySaved(delivery)
        ),
      };
    }

    // Mismos atributos que el retiro de /cart, más la sucursal preseleccionada en el checkout.
    buildPickupOrder(store, contact, documentData) {
      const attributes = Object.assign(this.baseAttributes(contact, documentData), {
        'Modalidad despacho': 'recojo-tienda',
        departamento: store.department || '',
        provincia: store.province || '',
        distrito: store.district || '',
        ubigeo: store.ubigeo || '',
        direccion: `Recojo Tienda: ${store.address || ''}`,
        referencia: '-',
        'tienda recojo': store.name,
      });

      // La dirección del cliente, si la dio: la elegida en esta sesión o la guardada de un envío
      // anterior.
      const person = {
        firstName: contact.firstName,
        lastName: contact.lastName,
        phone: contact.phone,
        company: this.companyLine(documentData),
      };
      const region = this.selectedOption('region');
      const province = this.selectedOption('province');
      const district = this.selectedOption('district');
      const address = this.addressFields.address.value.trim();
      const saved = this.saved || {};
      let customerAddress = null;
      if (region && province && district && district.dataset.code && address) {
        customerAddress = {
          address,
          reference: this.addressFields.reference.value.trim(),
          region: region.value,
          province: province.value,
          district: district.value,
          ubigeo: district.dataset.code,
        };
      } else if (saved.region && saved.city && saved.locality && saved.address) {
        customerAddress = {
          address: saved.address,
          reference: saved.reference && saved.reference !== '-' ? saved.reference : '',
          region: saved.region,
          province: saved.city,
          district: saved.locality,
        };
      }
      // Facturación: sus datos y su dirección; si no la dio, la de la tienda, como hacía /cart. Así
      // el checkout no pide escribir la dirección de facturación para pagar.
      const billing = Object.assign(
        {},
        person,
        customerAddress || {
          address: store.address || '',
          region: store.department || '',
          province: store.province || '',
          district: store.district || '',
          ubigeo: store.ubigeo || '',
        }
      );

      // Sin dirección de envío: en retiro cualquier dirección empuja el checkout a «Envío».
      const delivery = { deliveryMethod: ['PICK_UP'] };
      if (store.locationId) delivery.pickupHandle = [String(store.locationId)];

      return {
        mode: 'pickup',
        email: contact.email,
        attributes,
        // Sin ubigeo: si en el checkout pasa a «Envío», «Reveni Envíos» cobra según la dirección
        // que quede ahí, no según la tienda.
        line: { dispatchMode: 'recojo-tienda', shippingMethod: 'regular', ubigeo: '' },
        // countryCode es obligatorio para que Shopify acepte la sucursal preferida
        // (el país del comprador debe coincidir con el de la location).
        buyerIdentity: {
          countryCode: COUNTRY,
          email: contact.email,
          phone: toE164(contact.phone),
          preferences: { delivery },
        },
        billingAddress: buildShippingAddress(billing),
        // Por si en el checkout pasa a «Envío»: sus campos llegan con los datos del cliente y su
        // dirección si la dio; nunca la de la tienda.
        shippingPrefill: buildShippingAddress(Object.assign({}, person, customerAddress || {})),
        saved: Object.assign({ loaded: false }, this.contactSaved(contact), documentData.saved, this.pickupSaved(store)),
      };
    }

    goToCheckout(order) {
      writeCustomerData(order.saved);
      // En el carrito de la tienda se vacían los atributos que no aplican a este pedido (p. ej. la
      // fecha de un programado anterior); al checkout solo viajan los que tienen valor.
      const cartAttributes = {};
      ORDER_KEYS.forEach((key) => {
        cartAttributes[key] = '';
      });
      Object.keys(order.attributes).forEach((key) => {
        cartAttributes[key] = String(order.attributes[key] == null ? '' : order.attributes[key]).trim();
      });

      return fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: cartAttributes }),
      })
        .then((response) => response.json())
        // Un reintento ante un corte puntual de la Storefront API.
        .then((cart) => this.createCheckout(cart, order).catch(() => this.createCheckout(cart, order)))
        .then((checkoutUrl) => {
          let url = appendAddress(withCheckoutParams(checkoutUrl), 'billing_address', order.billingAddress);
          url = appendAddress(url, 'shipping_address', order.shippingPrefill);
          window.location.href = appendUTMs(url);
        });
    }

    createCheckout(cart, order) {
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
        return Promise.reject(new Error('Carrito vacío'));
      }

      const lines = cart.items.map((item) => {
        const existing = Object.entries(item.properties || {})
          .filter(([key, value]) => !LINE_KEYS.includes(key) && value != null && String(value) !== '')
          .map(([key, value]) => ({ key, value: String(value) }));
        return {
          merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity: item.quantity,
          // Solo claves con guion bajo: el checkout no las muestra bajo el producto.
          attributes: existing.concat(
            [
              { key: '_dispatch_mode', value: order.line.dispatchMode === 'recojo-tienda' ? 'pickup' : 'delivery' },
              { key: '_dispatch_mode_description', value: String(order.line.description || '') },
              { key: '_shipping_method', value: String(order.line.shippingMethod) },
              { key: '_ubigeo', value: String(order.line.ubigeo) },
              { key: '_peso', value: String(item.grams) },
              { key: '_sku', value: String(item.sku || '') },
            ].filter((attribute) => attribute.value !== '')
          ),
        };
      });

      const attributes = Object.keys(order.attributes)
        .filter((key) => String(order.attributes[key] == null ? '' : order.attributes[key]).trim() !== '')
        .map((key) => ({ key, value: String(order.attributes[key]).trim() }));
      const input = {
        attributes,
        lines,
        buyerIdentity: compact(order.buyerIdentity),
      };
      if (order.delivery) input.delivery = order.delivery;
      if (cart.note) input.note = cart.note;
      const discountCode = getAppliedDiscountCode(cart);
      if (discountCode) input.discountCodes = [discountCode];

      return storefront(CART_CREATE, { input }).then((response) => {
        const result = response && response.data && response.data.cartCreate;
        const errors = (response && response.errors) || (result && result.userErrors) || [];
        if (!result || !result.cart || errors.length) {
          throw new Error((errors[0] && errors[0].message) || 'cartCreate sin carrito');
        }
        return result.cart.checkoutUrl;
      });
    }
  }

  function init() {
    captureUTMs();
    document.querySelectorAll('.enova-minicart').forEach((drawer) => {
      if (drawer.dataset.enovaCheckoutReady === 'true') return;
      if (!drawer.querySelector('[data-enova-checkout-sheet]')) return;
      drawer.dataset.enovaCheckoutReady = 'true';
      new EnovaMinicartCheckout(drawer);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
